<template>
  <div id="research-coming" ref="popover" class="research-preview" popover="auto" role="region" aria-labelledby="research-coming-title" aria-describedby="research-coming-description" @beforetoggle="onBeforeToggle" @toggle="onToggle">
    <div ref="stage" class="preview-stage" :class="{ 'is-hovered': hovered }" @pointermove="followPointer" @pointerleave="resetTilt" @pointercancel="resetTilt">
      <article class="preview-card">
        <div class="preview-heading depth-title">
          <h3 id="research-coming-title">{{ copy.title }}</h3>
          <p id="research-coming-description">{{ copy.description }}</p>
        </div>
        <button class="preview-continue depth-action" type="button" popovertarget="research-coming" popovertargetaction="hide" autofocus>{{ copy.continue }}<span class="material-symbols-rounded" aria-hidden="true">arrow_forward</span></button>
      </article>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResearchPreview',
  props: { language: { type: String, default: 'zh' } },
  data() { return { isOpen: false, hovered: false, reducedMotion: false, motionQuery: null, pointerFrame: null, pointer: null } },
  computed: {
    copy() { return this.language === 'zh'
      ? { title: '下一步，值得期待。', description: '研发介绍页面正在准备中，更多关于产品与技术的探索即将呈现。', continue: '继续探索' }
      : { title: 'More is taking shape.', description: 'Our research page is in progress. More product and technology explorations are coming soon.', continue: 'Keep exploring' } }
  },
  mounted() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.reducedMotion = this.motionQuery.matches
    this.motionQuery.addEventListener('change', this.onMotionChange)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeUnmount() {
    this.isOpen = false
    this.resetTilt()
    this.motionQuery?.removeEventListener('change', this.onMotionChange)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    onBeforeToggle(event) {
      if (event.newState === 'closed') { this.isOpen = false; this.resetTilt() }
    },
    onToggle(event) {
      this.isOpen = event.newState === 'open'
      this.resetTilt()
    },
    onMotionChange(event) { this.reducedMotion = event.matches; this.resetTilt() },
    onVisibilityChange() { if (document.hidden) this.resetTilt() },
    followPointer(event) {
      if (!this.isOpen || this.reducedMotion || document.hidden || event.pointerType === 'touch') return
      this.pointer = { x: event.clientX, y: event.clientY }
      this.hovered = true
      if (this.pointerFrame !== null) return
      this.pointerFrame = window.requestAnimationFrame(() => {
        this.pointerFrame = null
        if (!this.isOpen || !this.pointer || this.reducedMotion || document.hidden) return
        // Measure the stationary stage, never the tilted card, to avoid feedback jitter.
        const stage = this.$refs.stage
        if (!stage) return
        const bounds = stage.getBoundingClientRect()
        if (!bounds.width || !bounds.height) { this.resetTilt(); return }
        const clamp = value => Math.max(-1, Math.min(1, value))
        const x = clamp((this.pointer.x - bounds.left) / bounds.width * 2 - 1)
        const y = clamp((this.pointer.y - bounds.top) / bounds.height * 2 - 1)
        stage.style.setProperty('--tilt-x', `${(-y * 7).toFixed(2)}deg`)
        stage.style.setProperty('--tilt-y', `${(x * 8).toFixed(2)}deg`)
      })
    },
    resetTilt() {
      if (this.pointerFrame !== null) window.cancelAnimationFrame(this.pointerFrame)
      this.pointerFrame = null
      this.pointer = null
      this.hovered = false
      this.$refs.stage?.style.setProperty('--tilt-x', '0deg')
      this.$refs.stage?.style.setProperty('--tilt-y', '0deg')
    }
  }
}
</script>

<style scoped>
.research-preview{position:fixed;inset:0;margin:auto;width:min(500px,calc(100% - 32px));padding:28px;border:0;background:transparent;color:var(--ink);opacity:0;transform:translateY(14px) scale(.98);transition:opacity .24s ease,transform .3s ease,display .3s allow-discrete,overlay .3s allow-discrete}
.research-preview:popover-open{opacity:1;transform:none}
@starting-style{.research-preview:popover-open{opacity:0;transform:translateY(14px) scale(.98)}}
.research-preview::backdrop{background:rgba(0,0,0,.38);opacity:0;transition:opacity .3s ease,display .3s allow-discrete,overlay .3s allow-discrete}
.research-preview:popover-open::backdrop{opacity:1}
@starting-style{.research-preview:popover-open::backdrop{opacity:0}}
.preview-stage{perspective:1100px;--tilt-x:0deg;--tilt-y:0deg}
.preview-card{position:relative;padding:42px 38px 36px;border:1px solid var(--line-strong);border-radius:24px;background:var(--page);text-align:center;transform-style:preserve-3d;transform:rotateX(var(--tilt-x)) rotateY(var(--tilt-y));box-shadow:0 24px 56px rgba(0,0,0,.25);transition:transform .45s cubic-bezier(.22,1,.36,1),border-color .3s,box-shadow .3s}
.is-hovered .preview-card{border-color:rgba(90,184,255,.45);box-shadow:0 28px 65px rgba(0,0,0,.3),0 0 40px rgba(90,184,255,.08);transition-duration:.18s,.3s,.3s}
.preview-heading{margin:0}
.preview-heading h3{font-size:clamp(26px,2.5vw,34px);font-weight:500;line-height:1.3;letter-spacing:-.035em}
.preview-heading p{margin:14px auto 0;max-width:370px;font-size:13px;line-height:1.85;color:var(--muted)}
.preview-continue{margin-top:30px;display:inline-flex;align-items:center;gap:8px;min-height:44px;padding:0 20px;border:1px solid var(--line-strong);border-radius:999px;background:var(--ink);color:var(--page);font-size:12px;white-space:nowrap;cursor:pointer}
.preview-continue .material-symbols-rounded{font-size:17px;transition:transform .25s}
.preview-continue:hover .material-symbols-rounded{transform:translateX(3px)}
button:focus-visible{outline:2px solid var(--accent);outline-offset:4px}
.depth-title,.depth-action{transform:translateZ(0);transition:transform .45s cubic-bezier(.22,1,.36,1)}
.is-hovered .depth-title{transform:translateZ(42px)}
.is-hovered .depth-action{transform:translateZ(24px)}
@media(prefers-reduced-motion:reduce){.research-preview,.research-preview::backdrop,.preview-card,.depth-title,.depth-action,.preview-continue .material-symbols-rounded{transition:none!important;transform:none!important}}
</style>
